/**
 * A distant, closed-parapet crossing and its planting beyond the walking
 * boundary (lifted from lantern-water's `northern-crossing.js`): two
 * embankment blocks, an arched deck of 24 sloped segments with parapets and
 * copings, end piers, and four trees on the far bank. Scenery only: the town
 * keeps its playable bridges. A world-coordinate set piece: placed at the
 * origin. Its own seed (93641). No params.
 */
import * as THREE from 'three';
import { seededRandom } from '../../grid.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'northern_crossing',
  buildPrefab(ctx) {
    const p = new THREE.Group();
    p.name = 'distant-northern-crossing';
    const { m, leafMats, box, mesh, cylinder, line, plane } = ctx;
    const random = seededRandom(93641);
    for (const [x, base] of [
      [0, 2],
      [14.5, 2.96],
    ] as const) {
      box(p, x, (base - 1.3) / 2, -32.5, 8, base + 1.3, 4, m.stone);
      box(p, x, base + 0.025, -32.5, 8, 0.05, 4, m.paving);
      for (const side of [-1, 1]) {
        box(p, x, base + 0.35, -32.5 + side * 1.86, 8, 0.7, 0.26, m.stone);
        box(p, x, base + 0.73, -32.5 + side * 1.86, 8.1, 0.09, 0.37, m.cream);
      }
    }
    const start = 3.35;
    const end = 10.95;
    const z = -31.9;
    const count = 24;
    const height = (x: number): number => {
      const t = Math.min(1, Math.max(0, (x - start) / (end - start)));
      return 2.05 + 0.96 * t + Math.sin(t * Math.PI) * 1.05;
    };
    for (let i = 0; i < count; i += 1) {
      const x0 = start + ((end - start) * i) / count;
      const x1 = start + ((end - start) * (i + 1)) / count;
      const y0 = height(x0);
      const y1 = height(x1);
      const x = (x0 + x1) / 2;
      const y = (y0 + y1) / 2;
      const slope = Math.atan2(y1 - y0, x1 - x0);
      const length = Math.hypot(x1 - x0, y1 - y0) + 0.008;
      const deck = box(p, x, y - 0.16, z, length, 0.32, 2.8, m.paving);
      deck.rotation.z = slope;
      for (const side of [-1, 1]) {
        for (const [offset, h, depth, material] of [
          [-0.46, 0.7, 0.4, m.stone],
          [0.33, 0.7, 0.3, m.stone],
          [0.73, 0.1, 0.42, m.cream],
        ] as const) {
          const block = box(p, x, y + offset, z + side * 1.39, length, h, depth, material);
          block.rotation.z = slope;
        }
      }
    }
    for (const x of [start + 0.15, end - 0.15]) {
      const top = height(x) + 0.77;
      for (const side of [-1, 1]) {
        box(p, x, (top - 1.3) / 2, z + side * 1.39, 0.6, top + 1.3, 0.6, m.stone);
        box(p, x, top + 0.07, z + side * 1.39, 0.76, 0.14, 0.76, m.cream);
      }
    }
    for (const [x, zz, base, scale] of [
      [1.7, -33.6, 2.05, 0.85],
      [-2.1, -33.6, 2.05, 1.05],
      [12.2, -33.6, 3.01, 0.95],
      [17.1, -33.6, 3.01, 1.1],
    ] as const) {
      cylinder(p, x, base + 1.4 * scale, zz, 0.13 * scale, 2.8 * scale, m.darkWood, 0.55);
      for (let j = 0; j < 5; j += 1) {
        const angle = j * 2.4;
        const spread = j === 0 ? 0 : 0.7 * scale;
        const xx = x + Math.cos(angle) * spread;
        const dz = zz + Math.sin(angle) * spread;
        const yy = base + (2.9 + random() * 0.5) * scale;
        line(p, [x, base + 1.6 * scale, zz], [xx, yy - 0.5 * scale, dz], 0.045 * scale, m.darkWood);
        const leaf = mesh(p, plane, leafMats[0], xx, yy, dz, 2.3 * scale, 2.25 * scale, 1);
        leaf.rotation.set(-0.32, (random() - 0.5) * 0.5, 0);
      }
    }
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
